import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./styles.scss";
import Typography from "@mui/material/Typography";
import { CircularProgress } from "@mui/material";
import {
  GET_SERVICES_TREND,
  GET_DEFAULT_LOCATION,
} from "../../graphql/queries";
import { useLazyQuery } from "@apollo/client";
import { getStoreChecker, getDate } from "../../utils/utils";
import { connect } from "react-redux";
import { Line } from "react-chartjs-2";
import ErrorMessage from "../ErrorMessage";
import {
  DATA_FETCH_ERROR,
  ONLOAN_INVALID_STOREID_ERROR,
} from "../../common/constants";

const ServicesTrend = ({ employeeInfo = {}, dateMatrix }) => {
  const [selectedLocation, setSelectedLocation] = useState(
    JSON.parse(sessionStorage.getItem("selectedLocation"))
  );
  let selectedStoreId = localStorage.getItem("storeId");
  const activeStorechecker = getStoreChecker(selectedStoreId);
  const [balloonsXaxis, setBalloonsXaxis] = useState([]);
  const [balloonsYaxisData, setBalloonsYaxisData] = useState([]);

  const [getDefaultLocation, { error: locationError = {} }] = useLazyQuery(
    GET_DEFAULT_LOCATION,
    {
      onCompleted: (data) => {
        let { salesSummaryV2 } = data;
        if (selectedLocation.locationName === "NA") {
          setSelectedLocation({
            locationNo: salesSummaryV2?.location?.primaryLocationNumber,
            locationName: salesSummaryV2?.location?.primaryLocationName,
          });
          sessionStorage.setItem(
            "selectedLocation",
            JSON.stringify({
              locationNo: salesSummaryV2?.location?.primaryLocationNumber,
              locationName: salesSummaryV2?.location?.primaryLocationName,
            })
          );
        }
      },
    }
  );

  const userEmail = employeeInfo.adUpn;

  useEffect(() => {
    if (userEmail && (activeStorechecker === true || selectedStoreId === "")) {
      getDefaultLocation({
        variables: { request: { email: userEmail, storeId: selectedStoreId } },
        context: {
          headers: {
            Authorization: localStorage.getItem("jwtToken"),
          },
        },
      });
    }
  }, [userEmail, getDefaultLocation, selectedStoreId, activeStorechecker]);

  const [
    getServicesTrendDetails,
    {
      data: { services: { servicesTrend = [] } = {} } = {},
      error: services_trend_error = {},
      loading: services_trend_loading,
    },
  ] = useLazyQuery(GET_SERVICES_TREND);

  useEffect(() => {
    if (
      userEmail &&
      dateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== "NA"
    ) {
      setBalloonsXaxis([]);
      setBalloonsYaxisData([]);
      getServicesTrendDetails({
        variables: {
          request: {
            email: userEmail,
            toDate: getDate("YESTERDAY", ""),
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            matrixType: dateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem("jwtToken"),
          },
        },
      });
    }
  }, [
    userEmail,
    dateMatrix,
    selectedLocation,
    getServicesTrendDetails,
    setBalloonsXaxis,
    setBalloonsYaxisData,
  ]);

  const serviceTrendData = servicesTrend;
  const { balloons } = serviceTrendData;

  useEffect(() => {
    let balloonsXaxis = [];
    let balloonsYaxis = [];

    if (balloons) {
      for (let i = 0; i < balloons?.keyList.length; i++) {
        balloonsXaxis.push(balloons?.keyList[i].key);
        balloonsYaxis.push(balloons?.keyList[i].value);
      }
    }

    setBalloonsXaxis(balloonsXaxis);
    setBalloonsYaxisData(balloonsYaxis);
  }, [balloons, setBalloonsXaxis, setBalloonsYaxisData]);

  const balloonsData = {
    labels: balloonsXaxis,
    datasets: [
      {
        axis: "y",
        data: balloonsYaxisData,
        borderColor: "#3333ff",
        barPercentage: 1,
        categoryPercentage: 0.5,
        hoverBackgroundColor: "rgba(255,99,132,0.4)",
        hoverBorderColor: "rgba(255,99,132,1)",

        fill: "start",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 150);
          gradient.addColorStop(0, "#367bff");
          gradient.addColorStop(0.5, "#669cff");
          gradient.addColorStop(1, "#cbddf6");

          return gradient;
        },
      },
    ],
  };
  const balloonsLineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";

            if (label) {
              label += ": ";
            }
            if (context.formattedValue !== null) {
              label += Math.abs(
                Number(context.formattedValue.replaceAll(",", ""))
              ).toLocaleString("en-US");
            }
            return label;
          },
        },
      },
      legend: {
        display: false,
        labels: {
          font: {
            size: 14,
          },
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          autoSkip: false,
          callback: function (value) {
            return Math.round(Math.abs(value * 100)) / 100;
          },
        },
      },
      x: {
        ticks: {
          autoSkip: false,
        },
      },
    },
  };

  return (
    <div data-testid="services-trend" className="services-trend-container">
      {services_trend_loading && (
        <div className="services-detail-loader">
          <CircularProgress color="primary" size={30} />
        </div>
      )}
      {!services_trend_loading && (
        <>
          {locationError.message || services_trend_error.message ? (
            <ErrorMessage
              statusCode={
                locationError?.networkError?.statusCode ||
                services_trend_error?.networkError?.statusCode
              }
              message={
                locationError?.networkError?.statusCode ||
                services_trend_error?.networkError?.statusCode
                  ? DATA_FETCH_ERROR
                  : services_trend_error.message
              }
            />
          ) : null}
          {activeStorechecker === false && selectedStoreId ? (
            <ErrorMessage message={ONLOAN_INVALID_STOREID_ERROR} />
          ) : null}
          <div className="services-splitScreen">
            <div className="services-topPane">
              <div className="innerTopPane">
                <div className="titleTopPane">
                  <div className="title-row-top">
                    <Typography
                      component="span"
                      className="services-trend-titles"
                    >
                      Balloon Services
                    </Typography>
                  </div>
                </div>
                <div className="panel-wrapper">
                  <div className="contentTopPane">
                    <Line
                      data={balloonsData}
                      options={balloonsLineChartOptions}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

ServicesTrend.propTypes = {
  employeeInfo: PropTypes.shape({
    adUpn: PropTypes.string,
  }),
  dateMatrix: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    employeeInfo: state.auth.employeeInfo,
    dateMatrix: state?.salesSummary?.currentDateMatrix?.value,
  };
};

export default connect(mapStateToProps, undefined)(ServicesTrend);
